/* =========================================================
   TGW Compare - Theme MVP
   Product selection, persistent tray, desktop modal and mobile modal.
   ========================================================= */

(function () {
  const STORAGE_KEY = 'tgw_compare_products';
  const MOBILE_QUERY = '(max-width: 749px)';

  const selectors = {
    root: '[data-tgw-compare-root]',
    toggle: '[data-tgw-compare-toggle]',
    productJson: '[data-tgw-compare-product-json]',
    tray: '[data-tgw-compare-tray]',
    trayProducts: '[data-tgw-compare-tray-products]',
    count: '[data-tgw-compare-count]',
    open: '[data-tgw-compare-open]',
    reset: '[data-tgw-compare-reset]',
    modal: '[data-tgw-compare-modal]',
    close: '[data-tgw-compare-close]',
    modalBody: '.tgw-compare-modal__body',
    attributes: '[data-tgw-compare-attributes]'
  };

  let root;
  let maxProducts = 5;
  let selectedProducts = [];
  let productMap = new Map();
  let compareAttributes = [];

  function init() {
    root = document.querySelector(selectors.root);
    if (!root) return;

    maxProducts = Number(root.dataset.maxProducts || 5);
    selectedProducts = loadSelectedProducts();
    compareAttributes = loadCompareAttributes();

    collectVisibleProducts();
    bindEvents();
    syncVisibleCheckboxes();
    renderTray();
  }

  /* =========================================================
     Data loading
     ========================================================= */

  function loadSelectedProducts() {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      return [];
    }
  }

  function saveSelectedProducts() {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(selectedProducts));
  }

  function loadCompareAttributes() {
    const script = root.querySelector(selectors.attributes);
    if (!script) return [];

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      return [];
    }
  }

  function collectVisibleProducts() {
    document.querySelectorAll(selectors.productJson).forEach((script) => {
      try {
        const product = JSON.parse(script.textContent);

        if (product && product.id) {
          productMap.set(String(product.id), product);
        }
      } catch (error) {
        // Ignore invalid product JSON.
      }
    });
  }

  /* =========================================================
     Events
     ========================================================= */

  function bindEvents() {
    document.addEventListener('change', function (event) {
      const toggle = event.target.closest(selectors.toggle);
      if (!toggle) return;

      const productId = String(toggle.dataset.productId || '');
      if (!productId) return;

      if (toggle.checked) {
        addProduct(productId, toggle);
      } else {
        removeProduct(productId);
      }
    });

    root.addEventListener('click', function (event) {
      const removeButton = event.target.closest('[data-tgw-compare-remove]');

      if (removeButton) {
        removeProduct(String(removeButton.dataset.tgwCompareRemove));
        return;
      }

      if (event.target.closest(selectors.reset)) {
        resetCompare();
        return;
      }

      if (event.target.closest(selectors.open)) {
        openModal();
        return;
      }

      if (event.target.closest(selectors.close)) {
        closeModal();
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        closeModal();
      }
    });

    window.addEventListener('resize', debounce(function () {
      const modal = root.querySelector(selectors.modal);

      if (modal && !modal.hidden) {
        renderModal();
      }
    }, 180));
  }

  /* =========================================================
     Product selection
     ========================================================= */

  function addProduct(productId, toggle) {
    if (selectedProducts.some((product) => String(product.id) === productId)) {
      return;
    }

    if (selectedProducts.length >= maxProducts) {
      toggle.checked = false;
      alert(`You can compare up to ${maxProducts} products.`);
      return;
    }

    const product = productMap.get(productId);

    if (!product) {
      toggle.checked = false;
      return;
    }

    selectedProducts.push(product);
    saveSelectedProducts();
    renderTray();
    syncVisibleCheckboxes();
  }

  function removeProduct(productId) {
    selectedProducts = selectedProducts.filter((product) => String(product.id) !== productId);

    saveSelectedProducts();
    renderTray();
    syncVisibleCheckboxes();

    if (selectedProducts.length === 0) {
      closeModal();
    } else {
      renderModal();
    }
  }

  function resetCompare() {
    selectedProducts = [];

    saveSelectedProducts();
    renderTray();
    syncVisibleCheckboxes();
    closeModal();
  }

  function syncVisibleCheckboxes() {
    const selectedIds = selectedProducts.map((product) => String(product.id));

    document.querySelectorAll(selectors.toggle).forEach((toggle) => {
      toggle.checked = selectedIds.includes(String(toggle.dataset.productId));
    });
  }

  /* =========================================================
     Tray
     ========================================================= */

  function renderTray() {
    const tray = root.querySelector(selectors.tray);
    const trayProducts = root.querySelector(selectors.trayProducts);
    const count = root.querySelector(selectors.count);

    if (!tray || !trayProducts || !count) return;

    if (selectedProducts.length === 0) {
      tray.hidden = true;
      trayProducts.innerHTML = '';
      count.textContent = '0 products selected';
      return;
    }

    tray.hidden = false;
    count.textContent = `${selectedProducts.length} ${selectedProducts.length === 1 ? 'product' : 'products'} selected`;

    trayProducts.innerHTML = selectedProducts.map((product) => {
      return `
        <div class="tgw-compare-tray-item">
          ${product.image ? `<img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title)}" loading="lazy">` : ''}
          <span>${escapeHtml(product.title)}</span>
          <button
            type="button"
            aria-label="Remove ${escapeHtml(product.title)}"
            data-tgw-compare-remove="${escapeHtml(String(product.id))}"
          >
            ×
          </button>
        </div>
      `;
    }).join('');
  }

  /* =========================================================
     Modal
     ========================================================= */

  function openModal() {
    if (selectedProducts.length < 2) {
      alert('Select at least 2 products to compare.');
      return;
    }

    const modal = root.querySelector(selectors.modal);
    if (!modal) return;

    renderModal();

    modal.hidden = false;
    document.documentElement.classList.add('tgw-compare-modal-open');
  }

  function closeModal() {
    const modal = root.querySelector(selectors.modal);
    if (!modal) return;

    modal.hidden = true;
    document.documentElement.classList.remove('tgw-compare-modal-open');
  }

  function renderModal() {
    const modalBody = root.querySelector(selectors.modalBody);
    if (!modalBody) return;

    modalBody.scrollTop = 0;

    if (isMobile()) {
      renderMobileModal(modalBody);
    } else {
      renderDesktopModal(modalBody);
      setupDesktopStickyTitles();
    }
  }

  /* =========================================================
     Desktop modal
     ========================================================= */

  function renderDesktopModal(modalBody) {
    modalBody.className = 'tgw-compare-modal__body';

    modalBody.innerHTML = `
      <div class="tgw-compare-modal__products" data-tgw-compare-modal-products></div>
      <div class="tgw-compare-modal__table" data-tgw-compare-table></div>
    `;

    const productsContainer = modalBody.querySelector('[data-tgw-compare-modal-products]');
    const tableContainer = modalBody.querySelector('[data-tgw-compare-table]');

    productsContainer.style.setProperty('--tgw-compare-columns', selectedProducts.length);
    tableContainer.style.setProperty('--tgw-compare-columns', selectedProducts.length);

    productsContainer.innerHTML = `
      <div class="tgw-compare-modal-spacer"></div>
      ${selectedProducts.map(renderDesktopProductCard).join('')}
    `;

    tableContainer.innerHTML = renderDesktopStickyTitles() + renderDesktopRows();
  }

  function renderDesktopProductCard(product) {
    return `
      <div class="tgw-compare-modal-product">
        <button
          type="button"
          class="tgw-compare-modal-product__remove"
          aria-label="Remove ${escapeHtml(product.title)}"
          data-tgw-compare-remove="${escapeHtml(String(product.id))}"
        >
          ×
        </button>

        ${product.image ? `<img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title)}" loading="lazy">` : ''}

        <div class="tgw-compare-modal-product__content">
          <h3>${escapeHtml(product.title)}</h3>
          ${product.price ? `<p class="tgw-compare-modal-product__price">${escapeHtml(product.price)}</p>` : ''}
          ${renderRating(product)}
          ${product.url ? `<a class="button tgw-compare-modal-product__button" href="${escapeHtml(product.url)}">View product</a>` : ''}
        </div>
      </div>
    `;
  }

  function renderDesktopStickyTitles() {
    return `
      <div class="tgw-compare-table__sticky-titles">
        <div class="tgw-compare-table__sticky-label"></div>
        ${selectedProducts.map((product) => {
          return `<div class="tgw-compare-table__sticky-title">${escapeHtml(product.title)}</div>`;
        }).join('')}
      </div>
    `;
  }

  function renderDesktopRows() {
  const groupedAttributes = [];
  const groupIndexMap = new Map();

  compareAttributes.forEach((attribute) => {
    const groupName = String(attribute.group || '');
    const groupKey = groupName.trim().toLowerCase();

    if (!groupIndexMap.has(groupKey)) {
      groupIndexMap.set(groupKey, groupedAttributes.length);
      groupedAttributes.push({
        group: groupName,
        attributes: []
      });
    }

    groupedAttributes[groupIndexMap.get(groupKey)].attributes.push(attribute);
  });

  return groupedAttributes.map((group) => {
    const groupMarkup = group.group
      ? `<div class="tgw-compare-table__group">${escapeHtml(group.group)}</div>`
      : '';

    const rowsMarkup = group.attributes.map((attribute) => {
      const values = selectedProducts.map((product) => {
        return `<div class="tgw-compare-table__value">${escapeHtml(getAttributeValue(product, attribute.key))}</div>`;
      }).join('');

      return `
        <div class="tgw-compare-table__row">
          <div class="tgw-compare-table__label">${escapeHtml(attribute.label)}</div>
          ${values}
        </div>
      `;
    }).join('');

    return `
      ${groupMarkup}
      ${rowsMarkup}
    `;
  }).join('');
}

  function setupDesktopStickyTitles() {
    const modalBody = root.querySelector(selectors.modalBody);
    const productsBlock = root.querySelector('[data-tgw-compare-modal-products]');

    if (!modalBody || !productsBlock) return;

    if (modalBody._tgwCompareStickyHandler) {
      modalBody.removeEventListener('scroll', modalBody._tgwCompareStickyHandler);
    }

    modalBody.classList.remove('tgw-compare-modal__body--show-sticky');

    const updateStickyState = function () {
      const shouldShow = modalBody.scrollTop > Math.max(120, productsBlock.offsetHeight - 40);
      modalBody.classList.toggle('tgw-compare-modal__body--show-sticky', shouldShow);
    };

    modalBody._tgwCompareStickyHandler = updateStickyState;
    modalBody.addEventListener('scroll', updateStickyState, { passive: true });

    updateStickyState();
  }

  /* =========================================================
     Mobile modal
     - Vertical scroll lives on modal body.
     - Horizontal scroll lives on mobile scroller.
     - Product-name sticky overlay appears after product cards disappear.
     ========================================================= */

  function renderMobileModal(modalBody) {
    modalBody.className = 'tgw-compare-modal__body tgw-compare-modal__body--mobile';

    modalBody.innerHTML = `
      <div class="tgw-compare-mobile-sticky-names" data-tgw-mobile-sticky-names>
        <div
          class="tgw-compare-mobile-sticky-names__inner"
          data-tgw-mobile-sticky-names-inner
          style="--tgw-compare-columns: ${selectedProducts.length};"
        >
          ${selectedProducts.map((product) => {
            return `<div class="tgw-compare-mobile-sticky-name">${escapeHtml(product.title)}</div>`;
          }).join('')}
        </div>
      </div>

      <div class="tgw-compare-mobile-scroller" data-tgw-mobile-scroller>
        <div class="tgw-compare-mobile-canvas" style="--tgw-compare-columns: ${selectedProducts.length};">
          <div class="tgw-compare-mobile-products-row">
            ${selectedProducts.map(renderMobileProductCard).join('')}
          </div>

          <div class="tgw-compare-mobile-product-end" data-tgw-mobile-product-end></div>

          ${renderMobileAttributeCanvas()}
        </div>
      </div>
    `;

    setupMobileStickyNames(modalBody);
  }

  function renderMobileProductCard(product) {
    return `
      <div class="tgw-compare-mobile-product">
        <button
          type="button"
          class="tgw-compare-mobile-product__remove"
          aria-label="Remove ${escapeHtml(product.title)}"
          data-tgw-compare-remove="${escapeHtml(String(product.id))}"
        >
          ×
        </button>

        ${product.image ? `<img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title)}" loading="lazy">` : ''}

        <h3>${escapeHtml(product.title)}</h3>

        ${product.price ? `<p class="tgw-compare-mobile-product__price">${escapeHtml(product.price)}</p>` : ''}

        ${renderRating(product)}

        ${product.url ? `<a class="button tgw-compare-mobile-product__button" href="${escapeHtml(product.url)}">View product</a>` : ''}
      </div>
    `;
  }

  function renderMobileAttributeCanvas() {
  const groupedAttributes = [];
  const groupIndexMap = new Map();

  compareAttributes.forEach((attribute) => {
    const groupName = String(attribute.group || '');
    const groupKey = groupName.trim().toLowerCase();

    if (!groupIndexMap.has(groupKey)) {
      groupIndexMap.set(groupKey, groupedAttributes.length);
      groupedAttributes.push({
        group: groupName,
        attributes: []
      });
    }

    groupedAttributes[groupIndexMap.get(groupKey)].attributes.push(attribute);
  });

  return groupedAttributes.map((group) => {
    const groupMarkup = group.group
      ? `
        <div class="tgw-compare-mobile-group-row">
          <span class="tgw-compare-mobile-sticky-text tgw-compare-mobile-sticky-text--group">
            ${escapeHtml(group.group)}
          </span>
        </div>
      `
      : '';

    const rowsMarkup = group.attributes.map((attribute) => {
      const values = selectedProducts.map((product) => {
        return `<div class="tgw-compare-mobile-value">${escapeHtml(getAttributeValue(product, attribute.key))}</div>`;
      }).join('');

      return `
        <div class="tgw-compare-mobile-attribute-row">
          <span class="tgw-compare-mobile-sticky-text tgw-compare-mobile-sticky-text--attribute">
            ${escapeHtml(attribute.label)}
          </span>
        </div>

        <div class="tgw-compare-mobile-values-row">
          ${values}
        </div>
      `;
    }).join('');

    return `
      ${groupMarkup}
      ${rowsMarkup}
    `;
  }).join('');
}

  function setupMobileStickyNames(modalBody) {
    const scroller = modalBody.querySelector('[data-tgw-mobile-scroller]');
    const sticky = modalBody.querySelector('[data-tgw-mobile-sticky-names]');
    const stickyInner = modalBody.querySelector('[data-tgw-mobile-sticky-names-inner]');
    const endMarker = modalBody.querySelector('[data-tgw-mobile-product-end]');

    if (!scroller || !sticky || !stickyInner || !endMarker) return;

    if (modalBody._tgwMobileScrollHandler) {
      modalBody.removeEventListener('scroll', modalBody._tgwMobileScrollHandler);
    }

    if (scroller._tgwMobileHorizontalHandler) {
      scroller.removeEventListener('scroll', scroller._tgwMobileHorizontalHandler);
    }

    let frame = null;

    const updateSticky = function () {
      frame = null;

      const bodyRect = modalBody.getBoundingClientRect();
      const markerRect = endMarker.getBoundingClientRect();
      const shouldShow = markerRect.top <= bodyRect.top + 4;

      sticky.classList.toggle('is-visible', shouldShow);
      stickyInner.style.transform = `translate3d(${-scroller.scrollLeft}px, 0, 0)`;
    };

    const requestUpdate = function () {
      if (frame) return;
      frame = window.requestAnimationFrame(updateSticky);
    };

    modalBody._tgwMobileScrollHandler = requestUpdate;
    scroller._tgwMobileHorizontalHandler = requestUpdate;

    modalBody.addEventListener('scroll', requestUpdate, { passive: true });
    scroller.addEventListener('scroll', requestUpdate, { passive: true });

    window.requestAnimationFrame(updateSticky);
  }

  /* =========================================================
     Shared helpers
     ========================================================= */

  function getAttributeValue(product, key) {
    if (
      !product.attributes ||
      product.attributes[key] === null ||
      product.attributes[key] === undefined ||
      product.attributes[key] === ''
    ) {
      return '—';
    }

    return String(product.attributes[key]);
  }

  function renderRating(product) {
    if (Number(product.ratingCount || 0) <= 0) {
      return '';
    }

    return `
      <div class="tgw-compare-modal-product__rating">
        <span class="tgw-compare-modal-product__stars">${renderStars(product.ratingValue)}</span>
        <span>(${escapeHtml(product.ratingCount)})</span>
      </div>
    `;
  }

  function renderStars(ratingValue) {
    const rating = Math.round(Number(ratingValue || 0));
    let stars = '';

    for (let index = 1; index <= 5; index++) {
      stars += index <= rating ? '★' : '☆';
    }

    return stars;
  }

  function isMobile() {
    return window.matchMedia(MOBILE_QUERY).matches;
  }

  function debounce(callback, delay) {
    let timer;

    return function () {
      window.clearTimeout(timer);
      timer = window.setTimeout(callback, delay);
    };
  }

  function escapeHtml(value) {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();